/**
 * FindingCounter — Real-time domain finding counter.
 *
 * Collapsed: small pill showing total finding count.
 * Expanded: grid showing count per domain with coverage gap highlighting.
 */

import { useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react'
import { SURVEY_DOMAINS } from '../../lib/services/survey'

interface FindingCounterProps {
  /** Map of domain → finding count */
  domainCounts: Record<string, number>
  /** Total findings across all domains */
  totalFindings: number
}

export function FindingCounter({ domainCounts, totalFindings }: FindingCounterProps) {
  const [expanded, setExpanded] = useState(false)

  if (totalFindings === 0) return null

  const zeroDomains = SURVEY_DOMAINS.filter((d) => !domainCounts[d])

  return (
    <div className="app-surface overflow-hidden">
      {/* Collapsed pill */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} className="text-amber-500" />
          <span className="text-xs font-bold text-slate-700">
            {totalFindings} finding{totalFindings !== 1 ? 's' : ''}
          </span>
          {zeroDomains.length > 0 && (
            <span className="text-[10px] text-red-500">
              {zeroDomains.length} domain{zeroDomains.length !== 1 ? 's' : ''} unchecked
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {/* Expanded grid */}
      {expanded && (
        <div className="px-3 pb-3 grid grid-cols-2 gap-1.5">
          {SURVEY_DOMAINS.map((domain) => {
            const count = domainCounts[domain] || 0
            return (
              <div
                key={domain}
                className={`px-2 py-1.5 rounded text-[11px] flex items-center justify-between ${
                  count > 0
                    ? 'bg-amber-50 text-amber-800'
                    : 'bg-red-50 text-red-400'
                }`}
              >
                <span className="font-bold">{domain}</span>
                <span>{count}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
